import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { URL, getToken } from "../../utils";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import RoleBadge from "../../components/RoleBadge";
import ConditionalRender from "../../components/ConditionalRender";

export default function UserDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const token = getToken();

  const fetchUser = useCallback(async () => {
    if (!id) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${URL}authentification/user-detail/${id}`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      setUser(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement de l'utilisateur");
      console.error("Erreur:", error);
      navigate("/users");
    } finally {
      setLoading(false);
    }
  }, [id, token, navigate]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const formatDate = (dateString) => {
    if (!dateString) return "Non renseigné";
    return new Date(dateString).toLocaleDateString("fr-FR", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const getCountryName = (countryCode) => {
    // Mapping simple pour les pays les plus communs
    const countries = {
      'TG': 'Togo',
      'BF': 'Burkina Faso',
      'CI': "Côte d'Ivoire",
      'GH': 'Ghana',
      'SN': 'Sénégal',
      'ML': 'Mali',
      'NE': 'Niger',
      'BJ': 'Bénin',
    };
    return countries[countryCode] || countryCode;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Utilisateur introuvable</h1>
          <Link 
            to="/users"
            className="text-amber-600 hover:text-amber-800"
          >
            Retour à la liste des utilisateurs
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <ToastContainer />
      
      {/* Header */}
      <div className="bg-white shadow-md rounded-lg mb-6">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate("/users")}
              className="text-gray-600 hover:text-gray-800"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            
            <div className="flex items-center space-x-4">
              <div className="flex-shrink-0 h-16 w-16">
                <div className="h-16 w-16 rounded-full bg-amber-100 flex items-center justify-center">
                  <span className="text-xl font-medium text-amber-600">
                    {(user.first_name?.[0] || user.username[0]).toUpperCase()}
                  </span>
                </div>
              </div>
              
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {user.first_name || user.last_name
                    ? `${user.first_name || ''} ${user.last_name || ''}`.trim()
                    : user.username}
                </h1>
                <p className="text-gray-500">@{user.username}</p>
                <div className="flex items-center space-x-2 mt-1">
                  <RoleBadge role={user.role} />
                  <span
                    className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      user.is_active
                        ? "bg-green-100 text-green-800"
                        : "bg-red-100 text-red-800"
                    }`}
                  >
                    {user.is_active ? "Actif" : "Inactif"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <ConditionalRender requiredPermissions="canManageUsers">
            <div className="flex space-x-2">
              <Link
                to={`/users/edit/${user.id}`}
                className="px-4 py-2 text-sm font-medium text-white bg-amber-600 border border-transparent rounded-md hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500"
              >
                Modifier
              </Link>
            </div>
          </ConditionalRender>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Informations personnelles */}
        <div className="bg-white shadow-md rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Informations personnelles</h2>
          </div>
          <div className="px-6 py-4 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <InfoItem label="Prénom" value={user.first_name} />
              <InfoItem label="Nom" value={user.last_name} />
              <InfoItem label="Email" value={user.email} />
              <InfoItem label="Téléphone" value={user.phone_number} />
              <InfoItem label="Poste" value={user.poste} />
              <InfoItem label="Pays" value={getCountryName(user.which_country)} />
            </div>
          </div>
        </div>

        {/* Informations système */}
        <div className="bg-white shadow-md rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Informations système</h2>
          </div>
          <div className="px-6 py-4 space-y-4">
            <InfoItem label="ID utilisateur" value={`#${user.id}`} />
            <InfoItem label="Nom d'utilisateur" value={user.username} />
            <InfoItem label="Rôle" value={<RoleBadge role={user.role} />} />
            <InfoItem 
              label="Dernière connexion" 
              value={formatDate(user.last_login)} 
            />
            <InfoItem 
              label="Date de création" 
              value={formatDate(user.date_creation)} 
            />
            <InfoItem 
              label="Date d'inscription" 
              value={formatDate(user.date_joined)} 
            />
          </div>
        </div>

        {/* Permissions et accès */}
        <div className="bg-white shadow-md rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Permissions et accès</h2>
          </div>
          <div className="px-6 py-4 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <PermissionItem 
                label="Accès administration" 
                value={user.is_staff}
                icon={user.is_staff ? "🔧" : "❌"}
              />
              <PermissionItem 
                label="Caissier" 
                value={user.is_caissier}
                icon={user.is_caissier ? "💳" : "❌"}
              />
              <PermissionItem 
                label="Super utilisateur" 
                value={user.is_superuser}
                icon={user.is_superuser ? "👑" : "❌"}
              />
              <PermissionItem 
                label="Compte actif" 
                value={user.is_active}
                icon={user.is_active ? "✅" : "❌"}
              />
            </div>
          </div>
        </div>

        {/* Magasin assigné */}
        <div className="bg-white shadow-md rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Magasin assigné</h2>
          </div>
          <div className="px-6 py-4">
            {user.magasin_nom ? (
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  <div className="h-10 w-10 rounded-md bg-blue-100 flex items-center justify-center">
                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">{user.magasin_nom}</p>
                  <p className="text-xs text-gray-500">ID: #{user.magasin}</p>
                </div>
              </div>
            ) : (
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  <div className="h-10 w-10 rounded-md bg-gray-100 flex items-center justify-center">
                    <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                    </svg>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Aucun magasin assigné</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Groupes et permissions spécifiques (si nécessaire) */}
      {(user.groups?.length > 0 || user.user_permissions?.length > 0) && (
        <div className="mt-6 bg-white shadow-md rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Groupes et permissions</h2>
          </div>
          <div className="px-6 py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {user.groups?.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-2">Groupes</h3>
                  <div className="space-y-1">
                    {user.groups.map((group, index) => (
                      <span key={index} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mr-2 mb-2">
                        Groupe #{group}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              {user.user_permissions?.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-2">Permissions spécifiques</h3>
                  <div className="space-y-1">
                    {user.user_permissions.map((permission, index) => (
                      <span key={index} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 mr-2 mb-2">
                        Permission #{permission}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Composant pour afficher une information
function InfoItem({ label, value }) {
  return (
    <div>
      <dt className="text-sm font-medium text-gray-500">{label}</dt>
      <dd className="mt-1 text-sm text-gray-900">
        {value || "Non renseigné"}
      </dd>
    </div>
  );
}

// Composant pour afficher une permission
function PermissionItem({ label, value, icon }) {
  return (
    <div className="flex items-center space-x-3">
      <span className="text-lg">{icon}</span>
      <div>
        <dt className="text-sm font-medium text-gray-500">{label}</dt>
        <dd className={`mt-1 text-sm font-medium ${value ? 'text-green-600' : 'text-gray-400'}`}>
          {value ? 'Oui' : 'Non'}
        </dd>
      </div>
    </div>
  );
}